// TrainHistogram.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import { Button, IconButton, Tooltip as MuiTooltip } from "@mui/material";
import { computeOverkill } from "../../utils/convertValidData.js";
import { Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { overkillCountState, overkillPercentState } from "../../recoil/index.ts";
import { calculateByThreshold } from "../../utils/utils.ts";
import { formatThreshold } from "../../utils/formatThreshold.ts";
import { Check } from "lucide-react";

/* 세로선 플러그인 (그리기 담당) */
const thresholdLinePlugin = {
  id: "thresholdLine",
  afterDatasetsDraw(chart, _args, pluginOptions) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const { top, bottom, left, right } = chartArea;

    let thr = Number(pluginOptions?.value);
    if (!Number.isFinite(thr)) return;
    thr = Math.max(0, Math.min(1, thr)); // 0~1

    const xPos = left + thr * (right - left);
    const color = pluginOptions?.color ?? "#e91e63";
    const lineWidth = pluginOptions?.lineWidth ?? 2;
    const dash = pluginOptions?.dash ?? [6, 4];
    const label = pluginOptions?.label ?? `Threshold: ${formatThreshold(thr)}`;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(xPos, top);
    ctx.lineTo(xPos, bottom);
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = color;
    ctx.setLineDash(dash);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = color;
    ctx.font = "12px sans-serif";
    ctx.textBaseline = "bottom";
    ctx.textAlign = xPos > (left + right) / 2 ? "right" : "left";
    const offsetX = xPos > (left + right) / 2 ? -6 : 6;
    ctx.fillText(label, xPos + offsetX, top + 14);
    ctx.restore();
  },
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, thresholdLinePlugin);

export default function TrainHistogram({
  totalCount,
  validDataset,
  bins = 100,
  myAIModule,
  thresholdCustom,
  setThresholdCustom,
  setIsThresholdUpdate,
  changeData,
  handleDataClick,
  handleApplyThreshold,
  fitHeight = false,
}) {
  const [overkillPercent, setOverkillPercent] = useRecoilState(overkillPercentState);
  const [overkillCount, setOverkillCount] = useRecoilState(overkillCountState);

  const defaultThreshold = myAIModule?.options?.labelscore?.para?.threshold_list?.[0] ?? 0.7;
  const { id } = useParams();
  const [thrLocal, setThrLocal] = useState(() =>
    clamp01(step01(typeof thresholdCustom === "number" ? thresholdCustom : defaultThreshold)),
  );

  const chartRef = useRef(null);
  const draggingRef = useRef(false);

  // 외부 값이 변하면(드래그 중이 아닐 때) 로컬 상태 동기화
  useEffect(() => {
    if (!draggingRef.current) {
      const next = clamp01(step01(typeof thresholdCustom === "number" ? thresholdCustom : defaultThreshold));
      setThrLocal(next);
    }
  }, [thresholdCustom, defaultThreshold]);

  // 히스토그램 데이터 계산 (path 배열 포함)
  const { labels, okHist, ngHist, okPathsByBin, ngPathsByBin, overkillPct } = useMemo(() => {
    const { overkillPercent } = computeOverkill(validDataset, thrLocal);
    const okList = [];
    const ngList = [];

    validDataset?.forEach?.((pack) => {
      const s = Number(pack.score);
      if (!Number.isFinite(s)) return;
      const score = Math.max(0, Math.min(1, s));
      const lab = (pack.label || "").toUpperCase();
      // const lab = (pack.AILabel || pack.label || "").toUpperCase();
      if (lab === "OK") okList.push({ score, path: pack.path });
      else if (lab === "NG") ngList.push({ score, path: pack.path });
    });

    const okHist = Array(bins).fill(0);
    const ngHist = Array(bins).fill(0);
    const okPathsByBin = Array.from({ length: bins }, () => []);
    const ngPathsByBin = Array.from({ length: bins }, () => []);

    const toIdx = (v) => {
      let i = Math.floor(v * bins);
      if (i < 0) i = 0;
      if (i >= bins) i = bins - 1;
      return i;
    };

    okList.forEach(({ score, path }) => {
      const idx = toIdx(score);
      okHist[idx]++;
      okPathsByBin[idx].push(path);
    });

    ngList.forEach(({ score, path }) => {
      const idx = toIdx(score);
      ngHist[idx]++;
      ngPathsByBin[idx].push(path);
    });

    const labels = Array.from({ length: bins }, (_, i) => (i / bins).toFixed(2));
    setOverkillPercent(overkillPercent);
    return {
      labels,
      okHist,
      ngHist,
      okPathsByBin,
      ngPathsByBin,
      overkillPct: overkillPercent,
    };
  }, [validDataset, bins, thrLocal]);

  const data = {
    labels,
    datasets: [
      {
        label: "OK",
        data: okHist,
        backgroundColor: "rgba(33, 150, 243, 0.8)",
        grouped: false,
        order: 2,
      },
      {
        label: "NG",
        data: ngHist,
        backgroundColor: "rgba(255, 152, 0, 0.8)",
        grouped: false,
        order: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: !fitHeight,
    animation: false,
    plugins: {
      title: false,
      legend: { position: "top" },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            const datasetLabel = context.dataset.label || "";
            const value = context.formattedValue;
            const binIndex = context.dataIndex;

            // if (datasetLabel === "NG") {
            //   const paths = ngPathsByBin[binIndex] || [];
            //   if (paths.length === 0) return `${datasetLabel}: ${value}`;
            //   // 모든 path를 줄바꿈으로 표시
            //   return [`${datasetLabel}: ${value}`, ...paths];
            // }
            return `${datasetLabel}: ${value}장`;
          },
        },
      },
      thresholdLine: {
        value: thrLocal,
        color: "#e91e63",
        lineWidth: 2,
        dash: [6, 4],
        label: `Threshold: ${formatThreshold(thrLocal)}`,
      },
    },
    scales: {
      x: {
        stacked: false,
        title: { display: true, text: "Score (0 ~ 1)" },
        ticks: { maxTicksLimit: 11 },
      },
      y: {
        stacked: false,
        beginAtZero: true,
        title: { display: true, text: "Count" },
      },
    },
    onClick: (event, elements, chart) => {
      if (!elements.length) return;
      const element = elements[0];
      const index = element.index; // 같은 bin index
      // setGraphScore(index);
      const score = chart.data.labels?.[index];

      const okPaths = okPathsByBin[index] || [];
      const ngPaths = ngPathsByBin[index] || [];

      const combined = [
        ...okPaths.map((p) => ({ label: "OK", path: p })),
        ...ngPaths.map((p) => ({ label: "NG", path: p })),
      ];
      handleDataClick(score, combined);
    },
  };

  // (드래그 이벤트 로직은 기존 그대로)
  useEffect(() => {
    const chart = chartRef.current;
    const canvas = chart?.canvas;
    if (!canvas || !chart?.chartArea) return;

    const hitPadding = 8;

    const pixelToValue = (px) => {
      const { left, right } = chart.chartArea;
      const ratio = (px - left) / (right - left);
      return clamp01(step01(ratio));
    };

    const getXPos = (value) => {
      const { left, right } = chart.chartArea;
      return left + clamp01(value) * (right - left);
    };

    const isOnLine = (px) => Math.abs(px - getXPos(thrLocal)) <= hitPadding;

    const setCursor = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      canvas.style.cursor = isOnLine(x) || draggingRef.current ? "col-resize" : "default";
    };

    const handleMouseDown = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      if (isOnLine(x)) {
        draggingRef.current = true;
        setCursor(e);
      }
    };

    const handleMouseMove = (e) => {
      setCursor(e);
      if (!draggingRef.current) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const next = pixelToValue(x);
      setThrLocal((prev) => {
        if (prev === next) return prev;
        chart.update("none");
        return next;
      });
    };

    const endDrag = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      canvas.style.cursor = "default";
      if (typeof setThresholdCustom === "function") {
        setThresholdCustom(thrLocal);
        const result = makeUpdateData(myAIModule, thrLocal);
        changeData(result);
        setIsThresholdUpdate(true);

        const currentList = validDataset;
        const { overkillCount } = calculateByThreshold(currentList, thrLocal);
        setOverkillCount(overkillCount);
      }
    };

    const makeUpdateData = (myModule, threshold) => {
      return {
        labelscore: {
          name: myModule?.options?.labelscore?.name,
          para: { threshold_list: [threshold] },
        },
      };
    };

    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", endDrag);

    return () => {
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", endDrag);
    };
  }, [thrLocal, setThresholdCustom]);

  // return <Bar ref={chartRef} data={data} options={options} />;
  return (
    <Box
      sx={{
        width: "100%",
        ...(fitHeight && { height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          textAlign: "center",
          gap: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <MuiTooltip title={`총: ${totalCount}장 / 과검 이미지: ${overkillCount}장`} arrow>
            <Typography
              fontSize={15}
              fontWeight={600}
              sx={{
                cursor: "help",
                display: "flex",
                alignContent: "center",
                width: "100%",
              }}
            >
              Overkill: {overkillPct} | Threshold: [{formatThreshold(thrLocal)}]
            </Typography>
          </MuiTooltip>
          <MuiTooltip title="해당 threshold를 기준으로 라벨링을 업데이트합니다">
            <IconButton variant="outlined" onClick={handleApplyThreshold}>
              <Check />
            </IconButton>
          </MuiTooltip>
        </div>
      </Box>
      {fitHeight ? (
        <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
          <Bar ref={chartRef} data={data} options={options} />
        </Box>
      ) : (
        <Bar ref={chartRef} data={data} options={options} />
      )}
    </Box>
  );
}

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}
function step01(v) {
  return Math.trunc(clamp01(v) * 10000) / 10000;
}

function getFileName(path) {
  if (typeof path !== "string") return "";
  return path.substring(path.lastIndexOf("/") + 1);
}
